import useSWR from 'swr';

const BANNER_APP_ID = import.meta.env.VITE_APP_ID;
const BANNER_V2_URL = 'https://wapi.holdstation.com/v1/banner/480';

// Feature flag: switch popups to the new banner API (only available on develop for now).
const USE_NEW_BANNER_API = import.meta.env.VITE_FEATURE_NEW_BANNER_API === 'true';

type BannerResponse = {
    sliderImages: {
        imageUrl: string;
        inAppBrowserUrl: string;
        isInApp: boolean;
        isDisplay: boolean;
        isPopup: boolean;
        orderId: number;
        navigateTo: string;
        appId: string;
        lang: string;
        id: number;
        oneTimeDisplay?: boolean;
    }[];
};

async function fetchBannerV2(lang: string, isPopup: boolean) {
    const response = await fetch(
        `${BANNER_V2_URL}/${BANNER_APP_ID}?is_popup=${isPopup}`,
        { headers: { 'Accept-Language': lang } }
    );

    if (!response.ok) {
        throw Error('Fetch fail');
    }
    const data = (await response.json()) as {
        slider_images: {
            id: number;
            app_id: string;
            image_url: string;
            in_app_browser_url: string;
            is_in_app: boolean;
            is_display: boolean;
            is_popup: boolean;
            one_time_display: boolean;
            order_id: number;
            navigate_to: string;
        }[];
    };

    // Map to the legacy shape; navigateTo is the in-app browser URL when is_in_app is true.
    return {
        sliderImages: (data.slider_images ?? [])
            .filter((item) => item.is_display)
            .map((item) => ({
                id: item.id,
                appId: item.app_id,
                lang,
                imageUrl: item.image_url,
                inAppBrowserUrl: item.in_app_browser_url,
                isInApp: item.is_in_app,
                isDisplay: item.is_display,
                isPopup: item.is_popup,
                oneTimeDisplay: item.one_time_display,
                orderId: item.order_id,
                navigateTo: item.is_in_app
                    ? item.in_app_browser_url
                    : item.navigate_to,
            })),
    } as BannerResponse;
}

async function fetchBanner(lang: string, isPopup: boolean) {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', BANNER_APP_ID);
    searchParams.set('lang', lang);
    searchParams.set('isPopup', String(isPopup));
    searchParams.set('isDisplay', 'true');

    const response = await fetch(
        `${
            import.meta.env.VITE_API_BANNER_URL
        }/api/banner-image-info/config?${searchParams.toString()}`
    );

    if (!response.ok) {
        throw Error('Fetch fail');
    }
    const data = await response.json();
    return data as BannerResponse;
}

export function usePopups(lang: string) {
    const response = useSWR(
        `/banner/${lang}/popup${USE_NEW_BANNER_API ? '/v2' : ''}`,
        () =>
            USE_NEW_BANNER_API
                ? fetchBannerV2(lang, true)
                : fetchBanner(lang, true),
        { dedupingInterval: 2000, revalidateOnFocus: false }
    );

    return response;
}
